import React, { useState, useEffect, useCallback } from "react";
import { map } from "rxjs";
import PropTypes from "prop-types";
import xstream from "state-x-stream";
import ZoomService from "../../services/zoomService";
import PositionEditor from "./PositionEditor";
import ls from "../../../../common/helpers/localStorageActions";
import Btn from "../../../../components/atoms/Btn";

function controller({ props$ }) {
  return {
    genomeId: props$.pipe(
      map((x) => x.zoomService),
      map((x) => x.genome?.genomeId)
    ),
  };
}

function PositionList({ zoomService, genomeId }) {
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    const items =
      genomeId === "hg38"
        ? ls.getPulsarPositionsHg38()
        : ls.getPulsarPositionsHg19();

    setPositions(items);
  }, [genomeId]);

  function save(item) {
    const posArr = [item, ...positions.filter((x) => x.pos !== item.pos)];
    setPositions(posArr);
    if (genomeId === "hg38") {
      ls.setPulsarPositionsHg38(posArr);
    } else {
      ls.setPulsarPositionsHg19(posArr);
    }
  }

  function edit(item) {
    const posArr = positions.map((x) => (x.pos === item.pos ? item : x));
    setPositions(posArr);
    if (genomeId === "hg38") {
      ls.setPulsarPositionsHg38(posArr);
    } else {
      ls.setPulsarPositionsHg19(posArr);
    }
  }

  function deletePos(item) {
    const posArr = positions.filter((x) => x !== item);
    setPositions(posArr);
    if (genomeId === "hg38") {
      ls.setPulsarPositionsHg38(posArr);
    } else {
      ls.setPulsarPositionsHg19(posArr);
    }
  }

  function validate(posStr = "") {
    try {
      return zoomService.parsePositionStr(posStr);
    } catch (e) {
      return null;
    }
  }

  function submit(rangeVal) {
    if (!rangeVal) return;
    const { d1, d2 } = zoomService.xScale$.value;
    const { start, end } = rangeVal;
    const isOutofCurrentRange = start < d1 || end > d2;
    if (isOutofCurrentRange) zoomService.setIsWholeGenome(true);
    zoomService.jumpTo(start, end);
  }

  const savePos = useCallback(() => {
    const pos = zoomService.getPositionStr();
    save({ pos, desc: "" });
  }, [zoomService, save]);

  return (
    <div className="tw-text-2xs tw-h-full">
      <div className="tw-h-12" />

      <div className="tw-text-center">
        <Btn onSubmit={savePos}>
          <div className="tw-flex tw-px-3 tw-rounded tw-py-2 tw-items-center tw-bg-primary tw-text-white tw-font-bold tw-opacity-90 hover:tw-opacity-100">
            + Bookmark Current Position
          </div>
        </Btn>
      </div>

      <div className="tw-h-10" />

      {positions && positions.length === 0 ? (
        <div className="tw-text-center tw-text-xs tw-opacity-70 tw-w-44 tw-mx-12">
          No saved positions
        </div>
      ) : null}

      {positions && positions.length ? (
        <div>
          <div className="tw-border-b border-1 tw-font-bold tw-opacity-70 tw-text-xs tw-flex">
            <div className="tw-w-4" />
            <div className="tw-w-48 tw-my-2">Position</div>
            <div className="tw-w-2" />
            <div className="tw-w-48 tw-my-2" colSpan={2}>
              Label
            </div>
            <div className="tw-w-4" />
          </div>
          <div>
            {positions.map((posItem) => (
              <PositionEditor
                select={(x) => {
                  const rangeVal = validate(x.pos);
                  if (rangeVal) submit(rangeVal);
                }}
                position={posItem}
                key={posItem.pos}
                submit={(x) => save(x)}
                edit={(x) => edit(x)}
                deletePos={(x) => deletePos(x)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

PositionList.propTypes = {
  genomeId: PropTypes.string.isRequired,
  zoomService: PropTypes.instanceOf(ZoomService).isRequired,
};

export default xstream(controller)(PositionList);
